import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ROLE_LABEL } from "@shared/domain";
import { fmtTime } from "@shared/dates";
import { initials } from "@shared/format";
import { useSignOut } from "@/lib/auth";
import { cn } from "@/lib/cn";
import { useScope, useUser } from "@/lib/queries";
import { useConnection } from "@/stores/connection";
import { queuedCount, useOutbox } from "@/stores/outbox";
import { savedAnswerCount, useTaskDraft } from "@/stores/taskDraft";
import { useUpkeepOutbox } from "@/stores/upkeepOutbox";
import { useUi } from "@/stores/ui";
import {
  AltArrowDownIcon,
  InfoCircleIcon,
  MagnifierIcon,
  LogoutIcon,
  MoonIcon,
  SunIcon,
} from "../icons";
import { usePageMeta } from "@/stores/page";
import { MenuItem, Popover } from "./Popover";
import { settingsFor } from "./navItems";
import { usePalette } from "@/stores/palette";

export function Wordmark({ compact }: { compact?: boolean }) {
  return (
    <Link
      to="/"
      className={cn(
        "flex min-w-0 flex-col gap-0.5 text-ink no-underline",
        !compact && "shrink-0",
      )}
    >
      <span
        className={cn(
          "font-display whitespace-nowrap font-semibold leading-[1.35]",
          compact ? "text-[19px]" : "text-[22px]",
        )}
      >
        {compact ? (
          "Kathkuni"
        ) : (
          <>
            काठकुनी <span className="opacity-55">/</span> Kathkuni
          </>
        )}
      </span>
      {!compact && (
        <span className="font-mono text-[10px] uppercase tracking-[.14em] text-subtle">
          Café &amp; Stay · PMS
        </span>
      )}
    </Link>
  );
}

export function PropertySwitcher({ compact }: { compact?: boolean }) {
  const { scope, properties, label } = useScope();
  const setScope = useUi((s) => s.setScope);
  const qc = useQueryClient();
  if (properties.length === 0) return null;
  const pick = (id: string) => {
    setScope(id);
    void qc.invalidateQueries();
  };
  return (
    <Popover
      trigger={({ toggle, open }) => (
        <button
          onClick={toggle}
          aria-expanded={open}
          className="kadil flex min-h-[44px] items-center gap-2.5 rounded-md border border-line-strong bg-sunk px-3 py-1.5 text-left hover:bg-canvas"
        >
          <span className="block size-2 shrink-0 rounded-full bg-deodar" />
          <span className="flex min-w-0 flex-col leading-[1.15]">
            {!compact && (
              <span className="font-mono text-[10px] uppercase tracking-[.12em] text-subtle">
                Property scope
              </span>
            )}
            <span className="truncate text-[14px] font-semibold">{label}</span>
          </span>
          <AltArrowDownIcon size={14} className="ml-1 shrink-0 text-subtle" />
        </button>
      )}
    >
      {(close) => (
        <>
          {properties.length > 1 && (
            <MenuItem
              active={scope === "all"}
              onClick={() => {
                pick("all");
                close();
              }}
            >
              <span className="flex-1">All properties</span>
              <span className="font-mono text-[11px] text-subtle">
                {properties.reduce((s, p) => s + p.roomCount, 0)} rooms
              </span>
            </MenuItem>
          )}
          {properties.map((p) => (
            <MenuItem
              key={p.id}
              active={scope === p.id}
              onClick={() => {
                pick(p.id);
                close();
              }}
            >
              <span className="flex flex-1 flex-col leading-tight">
                <span>{p.shortName}</span>
                <span className="text-[12px] text-subtle">{p.region}</span>
              </span>
              <span className="font-mono text-[11px] text-subtle">
                {p.roomCount} rooms
              </span>
            </MenuItem>
          ))}
        </>
      )}
    </Popover>
  );
}

/** Always visible: whether what you just did reached the server. */
export function ConnectionPill({ compact }: { compact?: boolean }) {
  const online = useConnection((s) => s.online);
  const last = useConnection((s) => s.lastContact);
  const items = useOutbox((s) => s.items);
  const flush = useOutbox((s) => s.flush);
  const jobs = useUpkeepOutbox((s) => s.jobs);
  const flushJobs = useUpkeepOutbox((s) => s.flush);
  const answers = useTaskDraft((s) => savedAnswerCount(s.drafts));
  const queued =
    queuedCount(items) + jobs.filter((j) => j.status !== "failed").length;
  // Offline in a room: say how much of the check is safe on this phone.
  const sub = online
    ? queued
      ? `Sending ${queued}`
      : last
        ? `Synced ${fmtTime(new Date(last))}`
        : "Connecting"
    : answers && !queued
      ? `${answers} answers saved here`
      : `Queued ${queued}`;
  return (
    <button
      onClick={() => {
        void flush();
        void flushJobs();
      }}
      title={
        online ? "Connected" : "Offline — bookings are kept on this device"
      }
      className={cn(
        "flex min-h-[44px] shrink-0 items-center gap-2.5 rounded-full border px-3.5",
        online
          ? "border-line-strong bg-sunk text-ink"
          : "border-brass bg-brass text-brass-ink",
      )}
    >
      <span
        className={cn(
          "block size-[9px] shrink-0 rounded-full",
          online
            ? "bg-pine shadow-[0_0_0_3px_color-mix(in_srgb,var(--pine)_22%,transparent)]"
            : "animate-pulse-k bg-brass-ink",
        )}
      />
      {/* Under 1100px the dot alone says it; the words would push the header onto two rows. */}
      {!compact && (
        <span className="hidden flex-col items-start leading-[1.15] min-[1100px]:flex">
          <span className="text-[13px] font-semibold">
            {online ? "Online" : "Offline"}
          </span>
          <span className="font-mono text-[10px] opacity-75">{sub}</span>
        </span>
      )}
    </button>
  );
}

export function ModeToggle({ className }: { className?: string }) {
  const mode = useUi((s) => s.mode);
  const toggle = useUi((s) => s.toggleMode);
  return (
    <button
      onClick={toggle}
      className={cn(
        "flex min-h-[44px] items-center gap-2 rounded-md border border-line-strong bg-sunk px-3 font-mono text-[12px] uppercase tracking-[.1em] hover:bg-canvas",
        className,
      )}
      aria-label={`Switch to ${mode === "day" ? "night" : "day"} mode`}
    >
      {mode === "day" ? <SunIcon size={18} /> : <MoonIcon size={18} />}
      {mode === "day" ? "Day" : "Night"}
    </button>
  );
}

/**
 * The account menu is where Settings lives: who you are, every Settings
 * section this role may open (grouped as in Settings), then Sign out. The
 * phone's More sheet shows the same body.
 */
export function AccountMenuBody({
  onNavigate,
  big,
}: {
  onNavigate: () => void;
  big?: boolean;
}) {
  const me = useUser();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const go = (to: string) => {
    onNavigate();
    void navigate({ to: to as "/" });
  };
  const row = cn(
    "flex w-full items-center gap-3 rounded-sm px-3 text-left",
    big ? "min-h-[46px] text-[15px]" : "min-h-[42px] text-[14px]",
  );
  return (
    <>
      <div
        className={cn(
          "flex items-center gap-3 border-b border-line pb-3",
          big ? "px-4" : "px-2.5 pt-1.5",
        )}
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-deodar font-mono text-[13px] font-semibold text-on-fill">
          {initials(me.name)}
        </span>
        <span className="flex min-w-0 flex-col leading-[1.3]">
          <span className="truncate text-[15px] font-bold">{me.name}</span>
          <span className="truncate text-[12.5px] text-muted">
            {ROLE_LABEL[me.role]} ·{" "}
            {me.role === "owner"
              ? "every property"
              : me.properties.map((p) => p.shortName).join(", ") ||
                "no property yet"}
          </span>
          <span className="truncate font-mono text-[11.5px] text-subtle">
            {me.email}
          </span>
        </span>
      </div>
      {settingsFor(me.role).map((g) => (
        <div key={g.group} className={cn("pt-2.5", big && "px-1.5")}>
          <div className="px-3 pb-1 font-mono text-[10px] uppercase tracking-[.14em] text-subtle">
            {g.group}
          </div>
          {g.sections.map((s) => {
            const Icon = s.icon;
            const on = path === s.to || path.startsWith(s.to + "/");
            return (
              <button
                key={s.id}
                role="menuitem"
                onClick={() => go(s.to)}
                className={cn(
                  row,
                  on ? "bg-deodar-tint font-semibold" : "hover:bg-sunk",
                )}
              >
                <Icon size={18} className={on ? "text-deodar" : "text-muted"} />
                <span className="flex-1">{s.label}</span>
              </button>
            );
          })}
        </div>
      ))}
      <div
        className={cn("mt-2.5 border-t border-line pt-1.5", big && "px-1.5")}
      >
        <button
          role="menuitem"
          onClick={() => {
            onNavigate();
            void signOut();
          }}
          className={cn(
            row,
            "text-rhodo hover:bg-[color-mix(in_srgb,var(--rhododendron)_9%,var(--surface))]",
          )}
        >
          <LogoutIcon size={18} /> Sign out
        </button>
      </div>
    </>
  );
}

function UserMenu() {
  const me = useUser();
  return (
    <Popover
      align="right"
      label="Account and settings"
      menuClassName="w-[300px] max-w-[calc(100vw-24px)] max-h-[calc(100vh-90px)]"
      trigger={({ toggle, open }) => (
        <button
          onClick={toggle}
          aria-expanded={open}
          aria-label="Account and settings"
          className="flex min-h-[44px] items-center gap-2.5 border-l border-line pl-4 text-left"
        >
          <span className="flex size-[34px] shrink-0 items-center justify-center rounded-full bg-deodar font-mono text-[12px] font-semibold text-on-fill">
            {initials(me.name)}
          </span>
          <span className="flex flex-col leading-[1.2]">
            <span className="text-[13px] font-semibold">{me.name}</span>
            <span className="text-[11px] text-subtle">
              {ROLE_LABEL[me.role]}
            </span>
          </span>
          <AltArrowDownIcon
            size={13}
            className={cn(
              "text-subtle transition-transform",
              open && "rotate-180",
            )}
          />
        </button>
      )}
    >
      {(close) => <AccountMenuBody onNavigate={close} />}
    </Popover>
  );
}

/** The screen's name, where the page used to spend a band on it; its how-to opens from ⓘ. */
function PageLabel({ compact }: { compact?: boolean }) {
  const meta = usePageMeta((s) => s.meta);
  if (!meta) return null;
  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-2",
        !compact && "border-l border-line pl-[18px]",
      )}
    >
      {meta.index && !compact && (
        <span className="font-mono text-[10px] tracking-[.18em] text-subtle">
          {meta.index}
        </span>
      )}
      <h1
        className={cn(
          "truncate font-semibold",
          compact ? "text-[13px]" : "text-[15px]",
          meta.tone === "rhodo" && "text-rhodo",
        )}
      >
        {meta.eyebrow}
      </h1>
      {meta.kicker && !compact && (
        <span className="hidden truncate font-mono text-[10.5px] uppercase tracking-[.1em] text-subtle min-[1100px]:inline">
          {meta.kicker}
        </span>
      )}
      {meta.lede && (
        <Popover
          trigger={({ toggle, open }) => (
            <button
              onClick={toggle}
              aria-expanded={open}
              aria-label="How this screen works"
              className="flex size-8 shrink-0 items-center justify-center rounded-full text-subtle hover:bg-sunk hover:text-ink"
            >
              <InfoCircleIcon size={17} />
            </button>
          )}
        >
          {() => (
            <p className="max-w-[340px] px-2.5 py-2 text-[13.5px] leading-[1.55] text-muted">
              {meta.lede}
            </p>
          )}
        </Popover>
      )}
    </div>
  );
}

export function Header() {
  const openPalette = usePalette((s) => s.setOpen);
  return (
    <header className="border-b border-line bg-surface">
      {/* Desktop */}
      <div className="hidden items-center gap-x-[18px] px-5 pb-3 pt-3.5 min-[900px]:flex">
        <Wordmark />
        <PropertySwitcher />
        <PageLabel />
        <div className="flex-1" />
        <button
          onClick={() => openPalette(true)}
          aria-label="Go to… (⌘K)"
          className="flex min-h-[44px] shrink-0 items-center gap-3 rounded-md border border-line-strong bg-sunk px-3.5 text-[14px] text-muted hover:text-ink min-[1000px]:w-[220px] min-[1300px]:w-[280px]"
        >
          <MagnifierIcon size={18} />
          <span className="hidden min-[1000px]:inline">Go to…</span>
          <kbd className="ml-auto hidden rounded-xs bg-surface px-1.5 py-0.5 font-mono text-[10px] text-subtle min-[1000px]:inline">
            ⌘K
          </kbd>
        </button>
        <ConnectionPill />
        <ModeToggle />
        <UserMenu />
      </div>
      {/* Phone */}
      <div className="flex items-center gap-2.5 px-4 py-2.5 min-[900px]:hidden">
        <Wordmark compact />
        <div className="min-w-0 flex-1">
          <PageLabel compact />
        </div>
        <PropertySwitcher compact />
        <ConnectionPill compact />
      </div>
    </header>
  );
}
